import React, { useMemo, useRef, useCallback, memo, useEffect } from 'react';
import classNames from 'classnames';
import type { Marker } from 'leaflet';
import { Popup } from 'components/Map';
import { useSelector } from 'react-redux';
import { getLocalDate } from '@surfline/web-common';

import { getMapZoom } from 'selectors/mapV2';
import useActiveWindStation from 'hooks/windStations/useActiveWindStations';
import { Speed } from 'types/units';
import { NearbyStation } from 'types/windStations';
import { convertToSeconds, oneHourSeconds } from 'utils/time';
import MapWindStationPinIcon from './MapWindStationPinIcon';

import styles from './MapWindStationPin.module.scss';

const className = (active: boolean) =>
  classNames(styles.pin, {
    [styles.pinActive]: active,
  });

interface Props {
  data: NearbyStation;
  units: Speed;
  hasLiveWindPermissions: boolean;
}

const MapWindStationPin: React.FC<Props> = ({ data, units, hasLiveWindPermissions }) => {
  const ref = useRef<Marker>(null);
  const {
    _id: stationId,
    stationName,
    location: { lat, lon },
  } = data;
  const position = useMemo(() => ({ lat, lon }), [lat, lon]);
  const [activeWindStationId, setActiveWindStationId] = useActiveWindStation();
  const active = activeWindStationId === stationId;
  const currentTimeInSeconds = Math.floor(
    convertToSeconds(getLocalDate(+new Date(), data?.lastObserved?.utcOffset || 0).getTime()) /
      1000,
  );

  const isOffline = currentTimeInSeconds - (data?.lastObserved?.timestamp || 0) > oneHourSeconds;
  const zoom = useSelector(getMapZoom);

  const showPinWithWindSpeed = zoom >= 9;

  // Effect that toggles the popup open whenever the marker becomes `active`
  useEffect(() => {
    if (active) {
      ref.current?.openPopup();
    } else {
      ref.current?.closePopup();
    }
  }, [active]);

  const onPopupOpen = useCallback(
    () => setActiveWindStationId(stationId),
    [setActiveWindStationId, stationId],
  );

  const eventHandlers = useMemo(
    () => ({
      popupopen: onPopupOpen,
      popupclose: () => {
        setActiveWindStationId(undefined);
      },
    }),
    [onPopupOpen, setActiveWindStationId],
  );

  const pinWidth = useMemo(() => {
    const windSpeed =
      typeof data?.lastObserved?.wind?.speed === 'number' ? data?.lastObserved?.wind?.speed : 0;

    if (isOffline) {
      return 18;
    }

    if (windSpeed < 10 && showPinWithWindSpeed) {
      return 70;
    }

    if (showPinWithWindSpeed) {
      return 86;
    }

    return 32;
  }, [data?.lastObserved?.wind?.speed, isOffline, showPinWithWindSpeed]);

  return (
    <MapWindStationPinIcon
      active={active}
      size={pinWidth}
      position={position}
      eventHandlers={eventHandlers}
      isOffline={isOffline}
      ref={ref}
      className={className(active)}
      zIndexOffset={0}
      data={data}
      units={units}
      showPinWithWindSpeed={showPinWithWindSpeed}
      hasLiveWindPermissions={hasLiveWindPermissions}
    >
      <Popup className={styles.pinPopup}>
        <h2 className={styles.pinPopupTitle}>{`${stationName}${isOffline ? ' (Offline)' : ''}`}</h2>
      </Popup>
    </MapWindStationPinIcon>
  );
};

export default memo(MapWindStationPin);
